// apps/web/src/pages/relatorios/relatorio-table.tsx — Fase 03, Plano 10 (REL-01, REL-02, 03-UI-SPEC §D).
import React, { Fragment, useMemo, useState } from 'react';
import { Layers } from 'lucide-react';
import { TablePagination } from '../../components/ui/table-pagination';
import { colunaSomavel, rotuloColuna, valorApresentacao } from '../../lib/relatorios/exportacao';

export interface ColunaDef {
  key: string;
  label: string;
  numeric?: boolean;
  format?: (v: unknown) => string;
}

interface RelatorioTableProps {
  data: unknown[];
  colunas?: ColunaDef[];
  pageSize?: number;
  mostrarTotais?: boolean;
}

export const RelatorioTable: React.FC<RelatorioTableProps> = ({
  data,
  colunas: colunasProp,
  pageSize: initialPageSize = 30,
  mostrarTotais = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  // Deriva colunas dinamicamente se não especificadas
  const colunas: ColunaDef[] = useMemo(() => {
    if (colunasProp && colunasProp.length > 0) return colunasProp;
    if (data.length === 0) return [];
    const first = data[0] as Record<string, unknown>;
    return Object.keys(first).map((key) => {
      const isNum = colunaSomavel(key, first[key]);
      return {
        key,
        label: rotuloColuna(key),
        numeric: isNum,
      };
    });
  }, [data, colunasProp]);

  // Calcula totais das colunas numéricas para linha de totais fixa (sticky)
  const totais = useMemo(() => {
    const res: Record<string, number> = {};
    // Saldo de extrato, quantidade repetida por etapa e unidades diferentes não admitem soma automática.
    if (!mostrarTotais) return res;
    for (const col of colunas) {
      if (col.numeric) {
        let sum = 0;
        for (const item of data) {
          const val = Number((item as Record<string, unknown>)[col.key] ?? 0);
          if (!isNaN(val)) sum += val;
        }
        res[col.key] = Number(sum.toFixed(2));
      }
    }
    return res;
  }, [data, colunas, mostrarTotais]);

  // Paginação simples de frontend / keyset de visualização
  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, currentPage, pageSize]);

  if (data.length === 0) {
    return (
      <div className="p-8 border border-dashed rounded-xl bg-slate-50 text-center space-y-2 text-xs text-slate-500">
        <Layers className="w-6 h-6 mx-auto text-slate-400" />
        <p className="font-semibold text-slate-700">
          Nenhum registro encontrado para os filtros selecionados.
        </p>
        <p className="text-[11px] text-slate-400">
          Tente ajustar os parâmetros de data ou outros filtros acima.
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-xl bg-white shadow-xs overflow-hidden flex flex-col text-xs font-sans">
      {/* Grade de Alta Densidade D5 com Virtualização / Scroll */}
      <div className="overflow-x-auto max-h-[500px]">
        <table className="w-full text-left border-collapse text-sm">
          <thead className="bg-slate-100 text-slate-700 uppercase font-bold tracking-wider sticky top-0 z-10 border-b">
            <tr>
              <th className="px-3 py-2 w-8 text-center">#</th>
              {colunas.map((col) => (
                <th
                  key={col.key}
                  className={`px-3 py-2 ${col.numeric ? 'text-right' : 'text-left'}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pagedData.map((item, idx) => {
              const row = item as Record<string, unknown>;
              const rowIdx = (currentPage - 1) * pageSize + idx + 1;
              const isExpanded = expandedRow === rowIdx;

              return (
                <Fragment key={rowIdx}><tr
                  tabIndex={0}
                  aria-label={'Registro ' + rowIdx + (isExpanded ? ', detalhes abertos' : ', abrir detalhes')}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedRow(isExpanded ? null : rowIdx); } }}
                  onClick={() => setExpandedRow(isExpanded ? null : rowIdx)}
                  className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                    isExpanded ? 'bg-slate-50/80 font-medium' : ''
                  }`}
                >
                  <td className="px-3 py-1.5 font-mono text-[10px] text-slate-400 text-center">
                    {rowIdx}
                  </td>
                  {colunas.map((col) => {
                    const rawVal = row[col.key];
                    const displayVal = col.format
                      ? col.format(rawVal)
                      : rawVal !== null && rawVal !== undefined
                      ? valorApresentacao(rawVal, col.key)
                      : '—';

                    return (
                      <td
                        key={col.key}
                        className={`px-3 py-1.5 max-w-[240px] ${
                          col.numeric ? 'text-right font-mono' : 'text-slate-800'
                        }`}
                        title={valorApresentacao(rawVal, col.key)}
                      >
                        {displayVal}
                      </td>
                    );
                  })}
                </tr>{isExpanded && <tr><td colSpan={colunas.length + 1} className="p-4"><dl className="grid sm:grid-cols-2 gap-3">{colunas.map(col => <div key={col.key}><dt className="text-xs text-muted-foreground">{col.label}</dt><dd className="break-all text-sm">{valorApresentacao(row[col.key], col.key)}</dd></div>)}</dl></td></tr>}</Fragment>
              );
            })}
          </tbody>

          {/* Linha de Totais Fixa (Sticky Footer) */}
          {Object.keys(totais).length > 0 && (
            <tfoot className="bg-slate-100 font-bold border-t-2 border-slate-300 sticky bottom-0 z-10 text-slate-900">
              <tr>
                <td className="px-3 py-2 text-center uppercase font-mono text-[10px]">Total</td>
                {colunas.map((col) => (
                  <td
                    key={col.key}
                    className={`px-3 py-2 ${col.numeric ? 'text-right font-mono' : 'text-left'}`}
                  >
                    {col.numeric ? (totais[col.key]?.toLocaleString('pt-BR') ?? '—') : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <TablePagination
        page={currentPage}
        pageSize={pageSize}
        totalItems={data.length}
        itemLabel="registros"
        onPageChange={setCurrentPage}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(1);
        }}
      />
    </div>
  );
};
